/**
 * ----------------------------------------------------------------------------------------------------------------------
 * GIFT WITH COUPON  (Impact theme)
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Re-implementation of the Focal "Rabattcode -> Gratis-Geschenk -> Stil im Dropdown wählen" feature, rebuilt to hook
 * cleanly into Impact's native cart pipeline (bundled section rendering + `cart:change`).
 *
 * Flow:
 *   1. Customer enters a code in <form is="coupon-form"> (cart drawer or cart page).
 *   2. The code is validated against the external service (kept from the previous setup).
 *   3. If valid AND the discount is a Buy-X-Get-Y type, the configured "unlisted" gift product is added to the cart
 *      (default Etui Klein; special codes -> Etui Medium / Safety Keeper; skip-codes get the discount but no gift).
 *   4. The Shopify discount code is applied server-side via /cart/update.js (so Shopify stays the source of truth for
 *      the actual price reduction — the theme only orchestrates UX).
 *   5. The cart re-renders through Impact's own `cart:change` event (no full reload in the drawer).
 *
 * The gift line item renders a <gift-style-select>: changing it stores the chosen colour/material as the "Variante"
 * line-item property via /cart/change.js.
 *
 * All configuration (validator URL, gift variant IDs, code->gift map, selectable source products, default styles)
 * lives in ONE place: snippets/gift-coupon-config.liquid.
 */

(() => {
  const configElement = document.getElementById('gift-coupon-config');
  if (!configElement) {
    return;
  }

  let CONFIG;
  try {
    CONFIG = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('[gift-coupon] Invalid configuration JSON:', error);
    return;
  }

  const root = window.Shopify?.routes?.root ?? '/';
  const settings = () => window.themeVariables?.settings ?? {};
  const isCartPage = () => settings().pageType === 'cart';
  const REQUEST_TIMEOUT = 10000;
  const propertyName = CONFIG.propertyName || 'Variante';

  /**
   * Normalise a user-entered code: lowercase, trimmed, restricted to a safe character set and capped in length.
   * This protects the external request and any later DOM usage.
   */
  const sanitizeCode = (raw) =>
    String(raw ?? '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\-_]/g, '')
      .slice(0, 64);

  /** fetch() with an abortable timeout so the UI can never hang on a slow endpoint. */
  async function timedFetch(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      return await fetch(url, { signal: controller.signal, ...options });
    } finally {
      clearTimeout(timer);
    }
  }

  const postJSON = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  /** Ask every mounted cart component (cart drawer, …) which section IDs need re-rendering. */
  function collectBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return [...new Set(sections)];
  }

  /** Broadcast the new cart so Impact's own components (drawer, count, free-shipping bar) re-render themselves. */
  function broadcastCartChange(cart, baseEvent = 'variant:add') {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { cart, baseEvent } })
    );
  }

  const getCart = async () =>
    (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();

  const TOAST_KEY = 'gift-coupon:toast';

  /**
   * Show a short, on-brand confirmation toast. It is appended to <body> (not the cart drawer), so it survives the
   * cart re-render. Also mirrored into Impact's screen-reader status region for accessibility.
   */
  function showToast(message) {
    if (!message) return;

    const announcement = document.getElementById('status-announcement');
    if (announcement) announcement.textContent = message;

    const toast = document.createElement('div');
    toast.className = 'gift-coupon-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('is-visible'));
    scheduleDismiss(toast);
  }

  /** Auto-dismiss a feedback element after a delay (fade out, then remove). */
  function scheduleDismiss(element, delay = 4000) {
    setTimeout(() => {
      element.classList.remove('is-visible');
      element.addEventListener('transitionend', () => element.remove(), { once: true });
      setTimeout(() => element.remove(), 500); // fallback if transitionend never fires
    }, delay);
  }

  /**
   * Show the success message INSIDE the cart drawer. The banner is prepended to the drawer footer (which persists
   * across the cart re-render). Falls back to the overlay toast if the drawer isn't open.
   */
  function showDrawerMessage(message) {
    const drawer = document.getElementById('cart-drawer');
    const footer = drawer?.querySelector('[slot="footer"]');

    if (!drawer?.hasAttribute('open') || !footer) {
      showToast(message);
      return;
    }

    const announcement = document.getElementById('status-announcement');
    if (announcement) announcement.textContent = message;

    footer.querySelector('.gift-coupon-banner')?.remove();

    const banner = document.createElement('div');
    banner.className = 'gift-coupon-banner';
    banner.setAttribute('role', 'status');
    banner.innerHTML =
      '<svg class="gift-coupon-banner__check" width="15" height="15" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4 9.5 7.5 13 14 5.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const text = document.createElement('span');
    text.textContent = message;
    banner.appendChild(text);

    footer.prepend(banner);
    requestAnimationFrame(() => banner.classList.add('is-visible'));
    scheduleDismiss(banner);
  }

  /** A toast queued right before a full page reload (cart page) is shown once the new page has loaded. */
  function flushPendingToast() {
    try {
      const pending = sessionStorage.getItem(TOAST_KEY);
      if (pending) {
        sessionStorage.removeItem(TOAST_KEY);
        showToast(pending);
      }
    } catch (error) {
      /* sessionStorage unavailable (e.g. private mode) — ignore */
    }
  }

  /**
   * Decide which gift (if any) a code grants. Mirrors the previous behaviour:
   * only Buy-X-Get-Y codes grant a gift, skip-codes get none, specific codes map to a specific gift,
   * everything else gets the default gift.
   */
  function giftIdForCode(code, type) {
    if (type !== 'DiscountCodeBxgy') return null;
    if ((CONFIG.skipCodes ?? []).includes(code)) return null;
    return CONFIG.codeGifts?.[code] ?? CONFIG.defaultGiftId ?? null;
  }

  async function validateCode(code) {
    const response = await timedFetch(CONFIG.validatorUrl, postJSON({ code }));
    if (!response.ok) throw new Error('validator_unreachable');

    const data = await response.json();
    if (data.error) throw new Error(data.message || 'validator_error');
    return data; // { valid, type, ... }
  }

  async function addGift(giftVariantId) {
    const style = CONFIG.defaultStyles?.[String(giftVariantId)];
    const item = { id: giftVariantId, quantity: 1 };
    if (style) item.properties = { [propertyName]: style };

    const response = await timedFetch(`${root}cart/add.js`, postJSON({ items: [item] }));
    if (!response.ok) throw new Error('gift_add_failed');
  }

  /** Apply the Shopify discount code (merged with any already-applied codes) and return the full, re-rendered cart. */
  async function applyDiscount(code, sections) {
    const cart = await getCart();
    const existing = (cart.discount_codes ?? []).filter((entry) => entry.applicable).map((entry) => entry.code);
    const discount = [...new Set([...existing, code])].join(',');

    const response = await timedFetch(`${root}cart/update.js`, postJSON({ discount, sections }));
    if (!response.ok) throw new Error('discount_failed');
    return response.json();
  }

  /* ============================================ COUPON FORM ============================================ */

  class CouponForm extends HTMLFormElement {
    #submitting = false;
    #abortController = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.addEventListener('submit', this.#onSubmit, { signal: this.#abortController.signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #onSubmit = async (event) => {
      event.preventDefault();
      if (this.#submitting) return;

      const field = this.querySelector('[name="coupon_code"]');
      const code = sanitizeCode(field?.value);

      this.#clearFeedback();
      if (!code) {
        this.#showError('Bitte gib einen Rabattcode ein.');
        field?.focus();
        return;
      }

      this.#setBusy(true);

      try {
        const result = await validateCode(code);

        if (!result.valid) {
          this.#showError('Dieser Rabattcode ist ungültig.');
          return;
        }

        const giftVariantId = giftIdForCode(code, result.type);
        let giftAdded = false;

        if (giftVariantId != null) {
          const cart = await getCart();
          const alreadyInCart = cart.items.some((item) => item.id === giftVariantId);
          if (!alreadyInCart) {
            await addGift(giftVariantId);
            giftAdded = true;
          }
        }

        const successMessage = giftAdded ? '🎁 Dein Geschenk wurde hinzugefügt!' : 'Rabattcode aktiviert.';

        // On the cart page Impact reloads after cart mutations — apply the discount, queue the toast, then reload.
        if (isCartPage()) {
          await applyDiscount(code);
          try {
            sessionStorage.setItem(TOAST_KEY, successMessage);
          } catch (storageError) {
            /* ignore */
          }
          window.location.reload();
          return;
        }

        const updatedCart = await applyDiscount(code, collectBundledSections());
        if (field) field.value = '';

        // The re-rendered drawer (new gift line + discount badge) plus the in-drawer banner are the success feedback.
        broadcastCartChange(updatedCart);
        document.getElementById('cart-drawer')?.show?.();
        showDrawerMessage(successMessage);
      } catch (error) {
        this.#showError('Es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
        if (error?.name !== 'AbortError') console.error('[gift-coupon]', error);
      } finally {
        this.#setBusy(false);
      }
    };

    #setBusy(busy) {
      this.#submitting = busy;
      this.querySelectorAll('[type="submit"]').forEach((button) => {
        button.toggleAttribute('aria-busy', busy);
        button.disabled = busy;
      });
    }

    #clearFeedback() {
      this.#showError(null);
    }

    #showError(message) {
      this.#setMessage('.coupon-form__error', message);
    }

    #setMessage(selector, message) {
      const element = this.querySelector(selector);
      if (!element) return;
      element.textContent = message ?? '';
      element.hidden = !message;
    }
  }

  if (!customElements.get('coupon-form')) {
    customElements.define('coupon-form', CouponForm, { extends: 'form' });
  }

  /* ========================================= GIFT STYLE SELECT ========================================= */

  class GiftStyleSelect extends HTMLElement {
    #select = null;
    #abortController = null;

    connectedCallback() {
      this.#select = this.querySelector('select');
      if (!this.#select) return;

      this.#abortController = new AbortController();
      this.#select.addEventListener('change', this.#onChange, { signal: this.#abortController.signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #onChange = async () => {
      const lineKey = this.getAttribute('line-key');
      if (!lineKey) return;

      const value = this.#select.value;
      const lineItem = this.closest('line-item');

      // Impact's LineItem loader assumes a thumbnail <pill-loader> exists, so only drive it when one is present.
      const notifyLineItem = (type, detail) => {
        if (lineItem?.querySelector('pill-loader')) {
          lineItem.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
        }
      };

      // Reuse Impact's own line-item loading state: a spinner overlay on the thumbnail, exactly like a quantity change.
      notifyLineItem('line-item:will-change', { targetQuantity: 1 });

      const body = { id: lineKey, quantity: 1, properties: { [propertyName]: value } };

      try {
        if (isCartPage()) {
          const response = await timedFetch(`${root}cart/change.js`, postJSON(body));
          if (!response.ok) throw new Error('style_change_failed');
          window.location.reload();
          return;
        }

        const response = await timedFetch(`${root}cart/change.js`, postJSON({ ...body, sections: collectBundledSections() }));
        if (!response.ok) throw new Error('style_change_failed');

        const cartContent = await response.json();
        // Mirror Impact's quantity-change completion: clearing the loader makes the pill-loader play its success
        // checkmark, then the drawer swaps the row in (1250ms later, via the 'line-item:change' base event).
        notifyLineItem('line-item:change', { quantity: 1, cart: cartContent });
        broadcastCartChange(cartContent, 'line-item:change');
      } catch (error) {
        notifyLineItem('line-item:error', {});
        if (error?.name !== 'AbortError') console.error('[gift-coupon]', error);
      }
    };
  }

  if (!customElements.get('gift-style-select')) {
    customElements.define('gift-style-select', GiftStyleSelect);
  }

  // Show a confirmation toast that was queued right before a cart-page reload.
  flushPendingToast();
})();